import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { jwtDecode } from 'jwt-decode';

import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    AccountTicketsNgrxFacade,
    ApplicationFacade,
    NotificationFacade,
    ShoppingCartService
} from '@ridango/components';
import { delay, filter, map, repeat, switchMap, take, timeout } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { ShoppingCartIconService } from '@ridango/components/lib/shopping-cart/shopping-cart-icon.service';
import { forkJoin, of } from 'rxjs';
import { AuthService } from '@ridango/core';
import { Capacitor } from '@capacitor/core';
import { Temporal } from '@js-temporal/polyfill';
import { CartStatus } from '@ridango/components/lib/purchase/purchase.model';

@Component({
    templateUrl: './payment-result-page-v2.component.html',
    styleUrls: ['./payment-result-page-v2.component.scss']
})
export class PaymentResultPageV2Component implements OnInit, AfterViewInit, OnDestroy {
    private readonly NOTIFICATION_TIMEOUT = 100;
    success: boolean;
    footer;
    public assetsPath$ = this.applicationFacade.assetsPath$;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private translate: TranslateService,
        private applicationFacade: ApplicationFacade,
        private notification: NotificationFacade,
        private shoppingCartService: ShoppingCartService,
        private shoppingCartIconService: ShoppingCartIconService,
        @Inject('ENV') public env: Env,
        private authService: AuthService,
        private accountTicketsFacade: AccountTicketsNgrxFacade
    ) {}

    ngOnInit() {
        forkJoin({
            routeParams: this.route.paramMap.pipe(take(1)),
            queryParams: this.route.queryParams.pipe(
                take(1),
                switchMap((params) => {
                    if (!this.env.IS_NEW_AFC) {
                        return of(params);
                    }
                    const originalParams = params;
                    return this.shoppingCartService.getShoppingCartStatus(params['cartId']).pipe(
                        repeat(),
                        delay(1000),
                        filter((cart) => {
                            const status = cart.status;
                            // Stop polling when status is terminal (FAILED, ABANDONED, CANCELLED, ACTIVE/DONE)
                            return (
                                status === CartStatus.FAILED ||
                                status === CartStatus.ABANDONED ||
                                status === CartStatus.CANCELLED ||
                                status === CartStatus.ACTIVE
                            );
                        }),
                        take(1),
                        timeout(60000),
                        map((cart) => {
                            this.shoppingCartService.setCartStatus(cart.status);
                            // Return status and ticketId, preserving original query params
                            return {
                                ...originalParams,
                                status: cart.status,
                                ticketId: cart.tickets.length === 1 ? cart.tickets[0].id : null
                            };
                        })
                    );
                })
            )
        }).subscribe(
            async ({ routeParams, queryParams }) => {
                const token = queryParams['result'];
                const zeroPricePayment = queryParams['zeroPricePayment'];
                const ticketId = routeParams.get('ticketId') ?? queryParams?.ticketId;
                const bankCardPersonalize = routeParams.get('bankCardPersonalize');

                try {
                    let cartStatus: CartStatus | null = null;

                    if (this.env.IS_NEW_AFC) {
                        // Status comes from polling result (queryParams.status) or route query params
                        cartStatus =
                            (queryParams.status as CartStatus) ||
                            (this.route.snapshot.queryParams['status'] as CartStatus);
                        this.success = cartStatus === CartStatus.ACTIVE;
                        if (
                            cartStatus &&
                            (cartStatus === CartStatus.FAILED ||
                                cartStatus === CartStatus.ABANDONED ||
                                cartStatus === CartStatus.CANCELLED)
                        ) {
                            await this.router.navigate([this.translate.currentLang, 'purchase-v2', 'cart'], {
                                replaceUrl: true
                            });
                            this.notification.error(this.translate.instant('PURCHASE_FLOW_RESULT_ERROR'));
                            return;
                        }
                    } else if (zeroPricePayment) {
                        this.success = true;
                    } else {
                        try {
                            const result: any = jwtDecode(token);
                            this.success = result?.status === CartStatus.ACTIVE;
                        } catch (e) {
                            this.success = false;
                        }
                    }

                    if (this.success) {
                        if (bankCardPersonalize) {
                            await this.router.navigate([this.translate.currentLang, 'account', 'cards']);
                            this.notification.success(this.translate.instant('BANKCARD_ADD_RESULT_SUCCESS'));
                            return;
                        }
                        this.shoppingCartIconService.initializeHeaderShoppingCart(0, null);
                        this.shoppingCartService.setCartId(null);

                        if (Capacitor.isNativePlatform()) {
                            this.updateTicketsCache();
                        }

                        ticketId
                            ? await this.router.navigate([this.translate.currentLang, 'account', 'tickets', ticketId])
                            : await this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
                        this.notification.success(this.translate.instant('PURCHASE_FLOW_RESULT_SUCCESS'), {
                            showAfterRouteChange: true
                        });
                        if (!this.env.IS_MOBILE_APP) {
                            this.notification.success(this.translate.instant('PURCHASE_FLOW_RESULT_SUCCESS'));
                        }
                    } else {
                        if (bankCardPersonalize) {
                            return await this.router
                                .navigate([this.translate.currentLang, 'account', 'cards'])
                                .then((navigationSuccess) => {
                                    if (navigationSuccess) {
                                        this.notification.error(this.translate.instant('BANKCARD_ADD_RESULT_FAILED'));
                                    }
                                });
                        }
                        // Navigate first, then show error notification (v2 popup style) for old AFC
                        await this.router.navigate([this.translate.currentLang, 'purchase-v2', 'cart']);
                        setTimeout(() => {
                            this.notification.error(this.translate.instant('PURCHASE_FLOW_RESULT_ERROR'));
                        }, this.NOTIFICATION_TIMEOUT);
                    }
                } catch (e) {
                    // Navigate first, then show error notification (v2 popup style)
                    await this.router.navigate([this.translate.currentLang, 'purchase-v2', 'cart'], {
                        replaceUrl: true
                    });
                    setTimeout(() => {
                        this.notification.error(this.translate.instant('PURCHASE_FLOW_RESULT_ERROR'));
                    }, this.NOTIFICATION_TIMEOUT);
                }
            },
            async () => {
                // Navigate first, then show error notification (v2 popup style)
                await this.router.navigate([this.translate.currentLang, 'purchase-v2', 'cart'], { replaceUrl: true });
                setTimeout(() => {
                    this.notification.error(this.translate.instant('PURCHASE_FLOW_RESULT_ERROR'));
                }, this.NOTIFICATION_TIMEOUT);
            }
        );
    }

    ngAfterViewInit() {
        this.footer = document.getElementsByTagName('ion-footer')[0];

        if (this.footer) {
            this.footer.style.display = 'none';
        }
    }

    ngOnDestroy() {
        if (this.footer) {
            this.footer.style.display = 'block';
        }
    }

    updateTicketsCache(): void {
        const isAuthenticated = this.authService.getLoginStatus();
        const expiredQrTicketsAvailable = this.env.EXPIRED_QR_TICKETS_AVAILABLE
            ? Temporal.Duration.from(this.env.EXPIRED_QR_TICKETS_AVAILABLE)
            : null;

        if (isAuthenticated) {
            this.accountTicketsFacade.refetchAllAccountTicketPages({
                limit: 10,
                expiredHours: expiredQrTicketsAvailable?.hours || 0,
                expiredMinutes: expiredQrTicketsAvailable?.minutes || 0
            });
        }

        if (Capacitor.isNativePlatform() && this.env.DEVICE_CUSTOMER_ENABLED) {
            if (isAuthenticated && !(this.env.SHOW_DEVICE_TICKET_ACCOUNT ?? true)) {
                return;
            }

            this.accountTicketsFacade.fetchDeviceTickets({
                expiredHours: expiredQrTicketsAvailable?.hours || 0,
                expiredMinutes: expiredQrTicketsAvailable?.minutes || 0
            });
        }
    }
}
